import { useRef, useState } from "react";
import {
  busStops,
  outboundBusSchedule,
  returnBusSchedule,
  scheduleVerifiedOn,
  type BusScheduleRow,
  type ScheduleTime,
} from "../../models/transport";

type Direction = "outbound" | "return";

const returnStops = [...busStops].reverse();
const xiadulanStopUrls: Record<Direction, string> = {
  outbound: "https://maps.app.goo.gl/xKuiGFd7gB8ACw7Z6",
  return: "https://maps.app.goo.gl/jyXtkDj4tbf1vR4s6",
};

function StopName({
  stop,
  direction,
  linked = true,
}: {
  stop: (typeof busStops)[number];
  direction: Direction;
  linked?: boolean;
}) {
  if (stop.key !== "xiadulan") return stop.zh;

  const label =
    direction === "outbound"
      ? "台東往下都蘭：下都蘭站牌位置（另開視窗）"
      : "下都蘭往台東：下都蘭站牌位置（另開視窗）";

  return (
    <>
      {stop.zh}
      {linked ? (
        <a
          className="stop-map-link"
          href={xiadulanStopUrls[direction]}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
        >
          📍
        </a>
      ) : (
        <b className="stop-map-pin" aria-hidden="true">
          📍
        </b>
      )}
    </>
  );
}

function TimeValue({ value }: { value?: ScheduleTime }) {
  if (!value) return <span className="schedule-empty">—</span>;
  return (
    <span className={`schedule-time is-${value.kind}`}>
      {value.kind === "estimated" && <small>約</small>}
      {value.time}
    </span>
  );
}

function RouteLink({ row }: { row: BusScheduleRow }) {
  return (
    <a
      className="route-badge"
      href={row.liveUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={`${row.route} 公車即時動態（另開視窗）`}
    >
      {row.route}
      <span aria-hidden="true">↗</span>
    </a>
  );
}

function OutboundHeader({ linked = true }: { linked?: boolean }) {
  return (
    <tr>
      <th scope="col" className="route-column">
        車號
        <span>BUS NO.</span>
      </th>
      {busStops.map((stop) => (
        <th
          scope="col"
          key={stop.key}
          className={stop.featured ? "featured-stop" : undefined}
        >
          <StopName stop={stop} direction="outbound" linked={linked} />
          <span>{stop.en}</span>
          {stop.note && <em>{stop.note}</em>}
        </th>
      ))}
    </tr>
  );
}

function OutboundTable() {
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  return (
    <div className="bus-table-shell">
      <div className="bus-table-sticky" aria-hidden="true">
        <div className="bus-table-sticky-scroll" ref={stickyHeaderRef}>
          <table className="bus-matrix">
            <thead>
              <OutboundHeader linked={false} />
            </thead>
          </table>
        </div>
      </div>
      <div
        className="bus-table-scroll"
        onScroll={(event) => {
          if (stickyHeaderRef.current)
            stickyHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
        }}
      >
        <table className="bus-matrix">
          <caption className="sr-only">
            台東轉運站經台東火車站與下都蘭往東海岸公車時刻
          </caption>
          <thead>
            <OutboundHeader />
          </thead>
          <tbody>
            {outboundBusSchedule.map((row, index) => (
              <tr key={`${row.route}-${row.stops.transfer?.time}-${index}`}>
                <th scope="row" className="route-column">
                  <RouteLink row={row} />
                  <small>往 {row.destination}</small>
                </th>
                {busStops.map((stop) => (
                  <td
                    key={stop.key}
                    className={
                      [
                        stop.featured ? "featured-stop" : "",
                        stop.key === "railway"
                          ? "emphasized-stop-time"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                  >
                    <TimeValue value={row.stops[stop.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReturnHeader({ linked = true }: { linked?: boolean }) {
  return (
    <tr>
      <th scope="col" className="route-column">
        車號
        <span>BUS NO.</span>
      </th>
      <th scope="col">
        起站發車
        <span>ORIGIN DEPARTURE</span>
      </th>
      {returnStops.map((stop) => (
        <th
          scope="col"
          key={stop.key}
          className={stop.featured ? "featured-stop" : undefined}
        >
          <StopName stop={stop} direction="return" linked={linked} />
          <span>{stop.en}</span>
          {stop.key === "xiadulan" ? (
            <em>從這裡上車</em>
          ) : (
            stop.note && <em>{stop.note}</em>
          )}
        </th>
      ))}
    </tr>
  );
}

function ReturnTable() {
  const stickyHeaderRef = useRef<HTMLDivElement>(null);

  return (
    <div className="bus-table-shell">
      <div className="bus-table-sticky" aria-hidden="true">
        <div className="bus-table-sticky-scroll" ref={stickyHeaderRef}>
          <table className="bus-matrix return-matrix">
            <thead>
              <ReturnHeader linked={false} />
            </thead>
          </table>
        </div>
      </div>
      <div
        className="bus-table-scroll"
        onScroll={(event) => {
          if (stickyHeaderRef.current)
            stickyHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
        }}
      >
        <table className="bus-matrix return-matrix">
          <caption className="sr-only">
            下都蘭經台東火車站往台東轉運站公車時刻
          </caption>
          <thead>
            <ReturnHeader />
          </thead>
          <tbody>
            {returnBusSchedule.map((row, index) => (
              <tr key={`${row.route}-${row.stops.xiadulan?.time}-${index}`}>
                <th scope="row" className="route-column">
                  <RouteLink row={row} />
                  {row.serviceNote && <small>{row.serviceNote}</small>}
                </th>
                <td className="origin-cell">
                  <span>{row.origin}</span>
                  <TimeValue value={row.originDeparture} />
                </td>
                {returnStops.map((stop) => (
                  <td
                    key={stop.key}
                    className={
                      [
                        stop.featured ? "featured-stop" : "",
                        stop.key === "railway"
                          ? "emphasized-stop-time"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                  >
                    <TimeValue value={row.stops[stop.key]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileSchedule({
  rows,
  direction,
}: {
  rows: BusScheduleRow[];
  direction: Direction;
}) {
  const outboundStops = busStops.filter(
    (stop) => !stop.extended || direction === "outbound",
  );
  const stops = direction === "outbound" ? outboundStops : returnStops;

  return (
    <div className="bus-card-list">
      {rows.map((row, index) => (
        <article
          className="bus-trip-card"
          key={`${direction}-${row.route}-${index}`}
        >
          <header>
            <RouteLink row={row} />
            <p>
              {direction === "outbound"
                ? `往 ${row.destination}`
                : row.destination}
            </p>
          </header>
          {direction === "return" && row.originDeparture && (
            <div className="mobile-origin">
              <span>{row.origin}・起站發車</span>
              <TimeValue value={row.originDeparture} />
            </div>
          )}
          <ol>
            {stops.map((stop) => {
              const value = row.stops[stop.key];
              if (!value && direction === "outbound" && "extended" in stop) {
                return null;
              }
              return (
                <li
                  key={stop.key}
                  className={
                    [
                      stop.featured ? "featured-stop" : "",
                      stop.key === "railway" ? "emphasized-stop-time" : "",
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                >
                  <span>
                    {stop.zh}
                    {stop.note && <small>{stop.note}</small>}
                  </span>
                  <TimeValue value={value} />
                </li>
              );
            })}
          </ol>
          {row.serviceNote && (
            <p className="trip-service-note">{row.serviceNote}</p>
          )}
        </article>
      ))}
    </div>
  );
}

export function TransportSchedule({
  mobileTable = false,
}: {
  mobileTable?: boolean;
}) {
  const [direction, setDirection] = useState<Direction>("outbound");
  const rows =
    direction === "outbound" ? outboundBusSchedule : returnBusSchedule;

  return (
    <section
      className={
        mobileTable
          ? "transport-schedule is-mobile-table"
          : "transport-schedule"
      }
      id="bus-schedule"
    >
      <div className="schedule-tabs" role="tablist" aria-label="公車行駛方向">
        <button
          type="button"
          role="tab"
          aria-selected={direction === "outbound"}
          className={direction === "outbound" ? "is-active" : undefined}
          onClick={() => setDirection("outbound")}
        >
          台東 → 下都蘭
          <span>TO DULAN</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={direction === "return"}
          className={direction === "return" ? "is-active" : undefined}
          onClick={() => setDirection("return")}
        >
          下都蘭 → 台東
          <span>TO TAITUNG</span>
        </button>
      </div>

      <p className="schedule-swipe-hint" aria-hidden="true">
        ← 左右滑動查看各站時間 →
      </p>

      <div className="schedule-desktop">
        {direction === "outbound" ? <OutboundTable /> : <ReturnTable />}
      </div>
      <div className="schedule-mobile">
        <MobileSchedule rows={rows} direction={direction} />
      </div>

      <div className="schedule-legend">
        <p>
          <strong>發車時間</strong>
          為起站公告時間；標示「約」的時間為沿途站牌預估到站時間。
        </p>
        <p>
          路況可能造成提早或延誤，請提前 10–15
          分鐘到站候車，出發當日務必查看公車即時動態。
        </p>
        <p>
          資料核對：{scheduleVerifiedOn}・營運以興東客運及公路客運官方公告為準。
        </p>
      </div>
    </section>
  );
}
